import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BellRing,
  Braces,
  CalendarClock,
  FolderClosed,
  Globe,
  Library,
  ShieldCheck,
  Sparkles,
  Table2,
  Wand2,
} from 'lucide-react';
import { Footer, Nav } from './LandingPage';
import { useSiteContent } from '@/components/marketing/usePublic';

/**
 * Halaman fitur yang menjelaskan tiap kemampuan dengan detail sungguhan -
 * bukan tiga kata pemasaran per kartu. Ditulis untuk pembaca yang sedang
 * membandingkan alat dan ingin tahu persis apa yang ia dapat.
 */
const FITUR = [
  {
    icon: Wand2,
    judul: 'Deteksi kolom otomatis',
    isi: 'Tempel URL, dan Crawel mencari daftar berulang di halaman itu: kartu produk, baris hasil pencarian, daftar artikel. Nama kolom, tipe data, dan contoh isinya diusulkan otomatis - Anda tinggal mengoreksi, bukan memulai dari nol. Tidak perlu tahu apa itu CSS selector.',
  },
  {
    icon: Sparkles,
    judul: 'Mode AI dengan kunci Anda sendiri',
    isi: 'Penamaan kolom bisa dibantu model Claude: "Kolom 1" menjadi "Nama Produk", lengkap dengan tipe datanya. Kuncinya milik Anda dan biayanya di akun Anthropic Anda, sehingga tidak ada markup dan tidak ada data halaman yang lewat akun kami. Isi halaman tidak pernah dikirim ke model - hanya nama tebakan dan tiga contoh nilai.',
  },
  {
    icon: Library,
    judul: 'Pustaka scraping siap pakai',
    isi: 'Tim dev bisa membungkus skrip Python menjadi formulir: pilih website, pilih bagiannya, isi parameternya, jalankan. Pengguna tanpa latar teknis mendapat hasil yang sama dengan yang ditulis engineer - karena memang skrip yang sama yang berjalan.',
  },
  {
    icon: Braces,
    judul: 'Dua mesin crawl',
    isi: 'Mesin statis membaca HTML mentah: hemat dan kilat. Mesin browser membuka halaman seperti Chrome sungguhan untuk situs yang memuat isinya lewat JavaScript. Mode otomatis mencoba yang cepat dulu dan hanya beralih bila halamannya kosong - Anda tidak perlu tahu bedanya untuk memakai keduanya.',
  },
  {
    icon: CalendarClock,
    judul: 'Penjadwalan',
    isi: 'Sekali jalan, per jam, harian, mingguan, bulanan, atau ekspresi cron. Jadwal tersimpan per job dan hasil tiap jalan tercatat terpisah, jadi Anda bisa membandingkan data hari ini dengan minggu lalu.',
  },
  {
    icon: FolderClosed,
    judul: 'Penyimpanan pribadi yang sesungguhnya',
    isi: 'Folder bersarang, unggah berkas sendiri, pratinjau isi sebagai tabel seperti lembar kerja, ganti nama, pindahkan antar folder, dan kirim ke pengguna lain lewat kode sekali pakai (sampai 100 MB sekali kirim). Hasil scraping, hasil skrip, dan unggahan manual hidup berdampingan dengan penanda asal yang jelas.',
  },
  {
    icon: Globe,
    judul: 'Data publik',
    isi: 'Publikasikan dataset dengan metadata lengkap: sumber, metode pengumpulan, frekuensi pembaruan, lisensi, dan penafian. Pengguna lain bisa membaca penjelasannya, melihat contoh barisnya, lalu mengimpornya ke folder pilihan mereka - tanpa duplikasi berkas di server.',
  },
  {
    icon: Table2,
    judul: 'Ekspor ke mana saja',
    isi: 'CSV untuk Excel, JSON untuk program, NDJSON untuk data besar. Ekspor dialirkan langsung dari basis data tanpa menampung di memori, jadi 150.000 baris sama ringannya dengan 100 baris.',
  },
  {
    icon: BellRing,
    judul: 'Progres langsung',
    isi: 'Halaman yang sedang di-crawl, jumlah baris yang sudah terkumpul, dan galat yang muncul - semuanya tampil saat itu juga, bukan setelah selesai. Notifikasi masuk saat job selesai atau gagal.',
  },
  {
    icon: ShieldCheck,
    judul: 'Crawling yang sopan',
    isi: 'robots.txt dihormati, ada jeda antar permintaan per domain, dan alamat internal jaringan diblokir dua lapis. Alat scraping mudah disalahgunakan; batasan ini tertulis terbuka, bukan disembunyikan di syarat dan ketentuan.',
  },
];

export const FiturPage = () => {
  const { konten } = useSiteContent();

  return (
    <div className="min-h-screen bg-[var(--color-void-950)] text-white">
      <Nav />

      <main className="mx-auto max-w-6xl px-5 pt-32 pb-20 sm:px-6">
        <p className="text-xs font-bold tracking-[0.14em] text-signal-400 uppercase">Fitur</p>
        <h1 className="display mt-4 max-w-2xl text-4xl font-extrabold sm:text-5xl">
          Yang Anda dapat, tanpa dibungkus jargon
        </h1>
        <p className="mt-5 max-w-lg text-[17px] leading-relaxed text-white/50">
          Setiap fitur di bawah ini sudah berjalan hari ini - yang masih rencana kami tandai
          "segera" di dalam aplikasi, bukan kami jual di sini.
        </p>

        <div className="mt-12 grid gap-4 md:grid-cols-2">
          {FITUR.map((f) => (
            <div
              key={f.judul}
              className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 transition-colors hover:border-white/16"
            >
              <span className="grid size-10 place-items-center rounded-lg bg-signal-400/12 text-signal-400">
                <f.icon className="size-5" aria-hidden="true" />
              </span>
              <h2 className="mt-4 text-lg font-bold">{f.judul}</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-white/55">{f.isi}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-4">
          <Link
            to="/daftar"
            className="inline-flex items-center gap-2 rounded-lg bg-signal-400 px-5 py-2.5 text-sm font-bold text-void-950 transition-colors hover:bg-signal-300"
          >
            Coba gratis
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
          <Link to="/harga" className="text-sm font-semibold text-white/60 hover:text-white">
            Lihat harga lengkap
          </Link>
        </div>
      </main>

      <Footer kontak={konten?.contactEmail} konten={konten} />
    </div>
  );
};
